// Storefront diagnostics only. Shopify's pixels own GA4 checkout and purchase events.
(() => {
  const isTest = document.currentScript.dataset.themeRole !== 'main';
  let analytics;
  let sdkRequested = false;
  let capturing = false;

  const allowed = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;

  function capture(event, properties) {
    if (capturing && allowed()) analytics.capture(event, properties);
  }

  // Reuse Motion's rendered product attributes and variant JSON, including quick views.
  function productProperties(section, variantId) {
    const variants = JSON.parse(section.querySelector('[data-variant-json]').value);
    const variant = variants.find(item => String(item.id) === String(variantId));
    return {
      product_id: section.dataset.productId,
      product_handle: section.dataset.productHandle,
      variant_id: variant?.id ?? null,
      variant_title: variant?.title ?? null,
      available: variant?.available ?? false,
    };
  }

  function productCardProperties(card) {
    return {
      product_id: card.dataset.productId,
      product_handle: card.dataset.productHandle,
      variant_id: card.dataset.variantId,
      variant_title: card.dataset.variantTitle,
      available: card.dataset.available === 'true',
    };
  }

  // Capture the requested combination before Motion substitutes a closest available match.
  document.addEventListener('change', event => {
    const input = event.target;
    if (!input.matches('[data-variant-input]')) return;
    const section = input.closest('[data-product-id]');
    const options = Array.from(section.querySelectorAll('[data-variant-input]'))
      .filter(item => item.type !== 'radio' || item.checked);
    const variants = JSON.parse(section.querySelector('[data-variant-json]').value);
    const variant = variants.find(item => options.every(option => item[option.dataset.index] === option.value));
    capture('product_option_selected', {
      ...productProperties(section, variant?.id),
      option_name: input.closest('.variant-input-wrap').dataset.handle,
      option_value: input.value,
    });
  }, true);

  document.addEventListener('ajaxProduct:adding', event => {
    const section = event.target.closest('[data-product-id]');
    capture('add_to_cart_attempted', {
      ...productProperties(section, event.detail.variantId),
      quantity: event.detail.quantity,
    });
  });

  document.addEventListener('ajaxProduct:result', event => {
    const section = event.target.closest('[data-product-id]');
    capture(event.detail.success ? 'add_to_cart_succeeded' : 'add_to_cart_failed', {
      ...productProperties(section, event.detail.variantId),
      quantity: event.detail.quantity,
      http_status: event.detail.httpStatus,
      failure_reason: event.detail.failureReason,
    });
  });

  // A product counts as exposed when its title enters the viewport, including on the homepage.
  const viewed = new WeakSet();
  const exposure = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting || !capturing || !allowed()) continue;
      const productElement = entry.target.closest('[data-product-id]');
      if (viewed.has(productElement)) continue;
      viewed.add(productElement);
      const properties = entry.target.matches('[data-roam-product-card]')
        ? productCardProperties(productElement)
        : productProperties(productElement, productElement.querySelector('[name="id"]').value);
      capture('product_exposed', properties);
      exposure.unobserve(entry.target);
    }
  });

  function observeProducts() {
    document.querySelectorAll('[data-product-id] .product-single__title, [data-roam-product-card]')
      .forEach(element => exposure.observe(element));
  }

  function syncConsent() {
    if (analytics) {
      if (!allowed()) {
        capturing = false;
        analytics.opt_out_capturing();
        exposure.disconnect();
      } else if (!capturing) {
        analytics.opt_in_capturing({ captureEventName: false });
        analytics.register({ is_test: isTest, integration: 'roam-theme-v1' });
        capturing = true;
        analytics.capture('$pageview');
        observeProducts();
      }
      return;
    }
    if (!allowed() || sdkRequested) return;
    sdkRequested = true;
    const script = document.createElement('script');
    script.src = 'https://us-assets.i.posthog.com/static/array.js';
    script.async = true;
    script.onload = () => window.posthog.init('phc_hON7BTbDFqw4PNz8YOPKQFm0yM3istzGQmvaou1VanM', {
      api_host: 'https://us.i.posthog.com',
      defaults: '2026-05-30',
      person_profiles: 'identified_only',
      opt_out_capturing_by_default: true,
      opt_out_persistence_by_default: true,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_dead_clicks: false,
      capture_performance: false,
      enable_recording_console_log: false,
      disable_surveys: true,
      session_recording: {
        maskAllInputs: true,
        recordHeaders: false,
        recordBody: false,
        ...(isTest ? { sampleRate: 1 } : {}),
      },
      before_send: event => allowed() ? event : null,
      loaded: instance => {
        analytics = instance;
        syncConsent();
      },
    });
    document.head.appendChild(script);
  }

  document.addEventListener('shopify:section:load', observeProducts);
  document.addEventListener('quickview:loaded', observeProducts);
  // Give the storefront a paint opportunity before loading analytics and replay.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    document.addEventListener('visitorConsentCollected', syncConsent);
    if (window.Shopify?.customerPrivacy) syncConsent();
    else window.Shopify?.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
      if (!error) syncConsent();
    });
  }));
})();
